import { useMemo, useState, useEffect, ChangeEvent, useRef } from 'react';
import {
    Box,
    Button,
    Center,
    Checkbox,
    Menu,
    Flex,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    ColumnDef,
    SortingFn,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { TOptionalProductDetails, TProductDetails } from 'types';
import { ChevronDownIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { EXCEL_FILE_TYPE } from 'shared/const';
import FileSaver from 'file-saver';
import { useDeletePDPsMutation, useGetPDPQuery, useLazyExportPDPsQuery } from 'core/API/pdpApi';
import { TRetailerProp } from 'shared/types';
import SelectColumnFilter from 'plugins/DITable/filters/SelectFilter';
import moment from 'moment';
import { linkStyle } from 'shared/styles';
import { isPDPPermissionError } from 'pages/pdp/errors';


export const nutriscoreColors = {
    A: '#108648',
    B: '#70C22C',
    C: '#F3C107',
    D: '#ED6F16',
    E: '#D93413',
}




const useProductDetails = ({ readonly, limitedAccess }: { readonly: boolean, limitedAccess?: boolean }) => {
    const [languages, setLanguages] = useState<string[]>([]);
    const [filters, setFilters] = useState<{ id: string; value: unknown }[]>([]);
    const todaysDate = useRef({
        week: moment(new Date()).isoWeek(),
        year: new Date().getFullYear(),
    });

    const [dateInWeeks, setDateInWeeks] = useState<{ week?: number, year?: number }>({
        week: moment(new Date()).isoWeek(),
        year: new Date().getFullYear(),
    });
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [retailer, setRetailer] = useState<TRetailerProp>('default');
    const [lang, setLang] = useState<string>('default');
    const translate = useTranslation();
    const [dataToSubmit, setDataToSubmit] = useState<TOptionalProductDetails>({ bulletPoints: [""] });
    const {
        data: pdpData,
        isLoading: isPDPLoading,
        isFetching: isPDPFetching,
        isError: isPDPError,
    } = useGetPDPQuery({
        lang,
        retailer: retailer === 'default' ? 'default' : retailer.id,
        date: dateInWeeks?.week?.toString().padStart(2, '0') + '_' + dateInWeeks?.year,
    });
    const [exportPDPs, { isLoading: isExportLoading, isFetching: isExportFetching }] =
        useLazyExportPDPsQuery();
    const [selectedPDPs, setSelectedPDPs] = useState<string[]>([]);
    const companyInfo = useGetCompanyInfoSelector();
    const [isDeleteMultiple, setIsDeleteMultiple] = useState(false);
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const deleteInitialRef = useRef(null);
    const toast = useToast();
    const [deletePDPs, { isLoading: isDeleteLoading }] = useDeletePDPsMutation();
    const userInfo = useGetUserInfoSelector();
    const [pdpsToDelete, setPdpsToDelete] = useState<string[]>([]);



    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);


    const data: Readonly<TOptionalProductDetails[]> = useMemo(() => {
        if (!isPDPLoading && !isPDPError && pdpData) {
            return pdpData;
        }
        return [];
    }, [isPDPLoading, isPDPError, pdpData]);

    const caseInsensitiveSort = useMemo(() => SORTING_FNS.caseInsensitive, []);
    const sortTypes: Record<string, SortingFn<any>> = useMemo(
        () => ({
            ...SORTING_FNS,
        }),
        [],
    );

    const defaultColumn = useMemo<Partial<ColumnDef<TProductDetails>>>(
        () => ({
            filterFn: 'includesString',
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const handleCloseDelete = () => {
        onDeleteClose();
    };

    const handleDelete = (multiple: boolean) => {
        setIsDeleteMultiple(multiple);
        onDeleteOpen();
    };

    const handleExportPDPs = async () => {
        try {
            const { data: res, isError: isExportError } = await exportPDPs({
                date: dateInWeeks?.week?.toString().padStart(2, '0') + '_' + dateInWeeks?.year,
                retailer: retailer === 'default' ? 'default' : retailer.id,
                lang,
            })

            if (isExportError || !res) throw new Error(translate('pdp.export.error') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });
            FileSaver.saveAs(blob, translate('pdp.export.file_name') as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleOpenModal = (type: 'add' | 'update') => {
        setModalType(type);
        setIsModalOpen(true);
    };

    const handleDeletePDPs = async () => {
        try {
            const deleteBody = isDeleteMultiple ? selectedPDPs : pdpsToDelete;
            await deletePDPs(deleteBody).unwrap();
            if (isDeleteMultiple) setSelectedPDPs([]);
            onDeleteClose();
            toast({
                title: translate('pdp.modal.deleteSuccesful'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: translate(isPDPPermissionError(error) ? 'pdp.error.permission_denied' : 'error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleCheckRow = (event: ChangeEvent<HTMLInputElement>, id: string) => {
        if (event.target.checked) setSelectedPDPs([...selectedPDPs, id]);
        else setSelectedPDPs(selectedPDPs.filter((e) => e !== id));
    };

    const handleCheckAllRows = (ids: string[]) => {
        setSelectedPDPs((selected) =>
            ids.every((id) => selected.includes(id)) ? [] : ids,
        );
    };


    const columns: Readonly<ColumnDef<TProductDetails, any>[]> = useMemo(() => {
        const tableColumns: readonly (ColumnDef<TProductDetails, any> | null)[] = [
            {
                header: ({ table }) => {
                    const ids = table.getFilteredRowModel().rows.map((row) => row.original._id);
                    const selectedCount = ids.filter((id) => selectedPDPs.includes(id)).length;
                    return (
                        <Center m={0}>
                            <Checkbox
                                onChange={() => handleCheckAllRows(ids)}
                                isIndeterminate={selectedCount > 0 && selectedCount < ids.length}
                                isChecked={ids.length > 0 && selectedCount === ids.length}
                                isDisabled={ids.length === 0}
                            ></Checkbox>
                        </Center>
                    );
                },
                id: 'selector',
                enableColumnFilter: false,
                cell: (info) => {
                    const cellValue = info.row.original as TProductDetails;
                    return (
                        <Box>
                            <Checkbox
                                onChange={(e) => {
                                    handleCheckRow(e, cellValue._id);
                                }}
                                isChecked={selectedPDPs.includes(cellValue._id)}
                            ></Checkbox>
                        </Box>
                    );
                },
                meta: {
                    width: '2%',
                },
            } as ColumnDef<TProductDetails, any>,

            {
                header: () => translate('pdp.column.ean') as string,
                accessorKey: 'ean',
                sortingFn: 'caseInsensitive',
                meta: {
                    Filter: DefaultColumnFilter,
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TProductDetails;
                    return (
                        <Box>
                            <Text
                                {...linkStyle}
                                onClick={() => {
                                    setDataToSubmit({
                                        _id: cellValue._id,
                                        ean: cellValue.ean,
                                        label: cellValue.label,
                                        description: cellValue.description,
                                        bulletPoints: cellValue.bulletPoints,
                                        nutriscore: cellValue.nutriscore,
                                        week: cellValue.week,
                                    });
                                    handleOpenModal('update');
                                }
                                }
                            >{cellValue.ean}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TProductDetails, any>,

            {
                header: () => translate('pdp.column.label') as string,
                accessorKey: 'label',
                sortingFn: 'caseInsensitive',
                meta: {
                    Filter: DefaultColumnFilter,
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TProductDetails;
                    return (
                        <Box>
                            <Text>{cellValue.label || '-'}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TProductDetails, any>,

            {
                header: () => translate('pdp.column.description') as string,
                accessorKey: 'description',
                sortingFn: 'caseInsensitive',
                meta: {
                    Filter: DefaultColumnFilter,
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TProductDetails;
                    const descriptionToShow = cellValue.description && cellValue.description.length > 100 ? `${cellValue.description.substring(0, 100)}...` : cellValue.description;
                    return (
                        <Box>

                            <Text>{descriptionToShow || '-'}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TProductDetails, any>,


            {
                header: () => translate('pdp.column.bulletPoint') as string,
                id: 'bulletPoints',
                accessorFn: (row: TProductDetails) => row.bulletPoints?.join(', ') ?? '',
                sortingFn: 'caseInsensitive',
                meta: {
                    Filter: DefaultColumnFilter,
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TProductDetails;

                    const bulletPointsLength = cellValue.bulletPoints && cellValue?.bulletPoints?.length || 0;
                    const bulletPointsToShow = cellValue.bulletPoints ? bulletPointsLength > 3 ? cellValue.bulletPoints?.slice(0, 3).join(', ') + '...' : cellValue.bulletPoints?.join(', ') : '-';
                    return (
                        <Box>
                            <Text>{bulletPointsToShow}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TProductDetails, any>,


            {
                header: () => translate('pdp.column.nutriscore') as string,
                accessorKey: 'nutriscore',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                    Filter: SelectColumnFilter,
                },
                cell: (info) => {
                    const cellValue = info.row.original as TProductDetails;
                    return (
                        <Box>
                            <Text>

                                <Flex alignItems="center" m="4px">
                                    {Object.entries(nutriscoreColors).map(([score, color]) => (
                                        <Box
                                            key={score}
                                            width={cellValue.nutriscore === score ? '36px' : '32px'}
                                            height={cellValue.nutriscore === score ? '38px' : '28px'}
                                            bg={
                                                cellValue.nutriscore === score
                                                    ? color
                                                    : `${color}50`
                                            }
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            color="white"
                                            fontWeight="bold"
                                            cursor="default"
                                            borderRadius={cellValue.nutriscore === score ? '8px' : '0'}
                                            border={cellValue.nutriscore === score ? '2px solid white' : 'none'}
                                        >
                                            {score}
                                        </Box>
                                    ))}
                                </Flex>
                            </Text>
                        </Box>
                    );
                },
            } as ColumnDef<TProductDetails, any>,

            {
                header: () => translate(`list.column.actions`) as string,
                accessorKey: 'actions',
                enableColumnFilter: false,
                enableSorting: false,
                meta: {
                    width: '10%',
                },
                cell: (info) => {
                    const disabled = readonly || (limitedAccess && (todaysDate.current.week !== dateInWeeks.week || todaysDate.current.year !== dateInWeeks.year));
                    const cellValue = info.row.original as TProductDetails;
                    return (
                        <Flex>
                            <Menu size="xs">
                                <MenuButton size="xs" as={Button} rightIcon={<ChevronDownIcon />}>
                                    {translate(`list.menu.button.actions`)}
                                </MenuButton>
                                <MenuList>
                                    <MenuItem
                                        onClick={async () => {
                                            setPdpsToDelete([cellValue._id]);
                                            handleDelete(false);
                                        }}
                                        icon={<DeleteIcon />}
                                        isDisabled={disabled}
                                    >
                                        {translate('pdp.menu.button.delete')}
                                    </MenuItem>

                                    <MenuItem
                                        onClick={async () => {
                                            setDataToSubmit({
                                                _id: cellValue._id,
                                                ean: cellValue.ean,
                                                label: cellValue.label,
                                                description: cellValue.description,
                                                bulletPoints: cellValue.bulletPoints,
                                                nutriscore: cellValue.nutriscore,
                                                week: cellValue.week,
                                            });
                                            handleOpenModal('update');
                                        }}
                                        icon={<EditIcon />}
                                        isDisabled={disabled}
                                    >
                                        {translate('pdp.menu.button.edit')}
                                    </MenuItem>
                                </MenuList>
                            </Menu>
                        </Flex>
                    );
                },
            } as ColumnDef<TProductDetails, any>,
        ];

        const filteredColumns = tableColumns.filter((e): e is ColumnDef<TProductDetails, any> => e !== null);
        return filteredColumns;
    }, [pdpData, selectedPDPs, languages, userInfo, readonly]);

    const table = useReactTable<TProductDetails>({
        columns: columns as ColumnDef<TProductDetails, any>[],
        data: data as TProductDetails[],
        defaultColumn,
        sortingFns: sortTypes,
        initialState: { columnFilters: filters },
        getCoreRowModel: getCoreRowModel(),
        getColumnCanGlobalFilter: (column) => column.getCanFilter(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filteredRows = table.getFilteredRowModel().rows;

    useEffect(() => {
        const filteredIds = new Set(filteredRows.map((row) => row.original._id));
        setSelectedPDPs((selected) => {
            const next = selected.filter((id) => filteredIds.has(id));
            return next.length === selected.length ? selected : next;
        });
    }, [filteredRows]);

    useEffect(() => {
        setFilters(table.getState().columnFilters);
    }, [table.getState().columnFilters]);

    const props = useMemo(
        () => ({
            languages,
            filters,
            retailer,
            setRetailer,
            lang,
            setLang,
            retailers,
            isRetailersLoading,
            isRetailerError,
            dataToSubmit,
            columns,
            table,
            pdpData,
            isPDPLoading,
            isPDPFetching,
            isPDPError,
            selectedPDPs,
            setSelectedPDPs,
            companyInfo,
            userInfo,
            defaultColumn,
            dateInWeeks,
            setDateInWeeks,
            todaysDate,
            data,
            isDeleteOpen,
            handleCloseDelete,
            isDeleteLoading,
            handleDeletePDPs,
            isDeleteMultiple,
            deleteInitialRef,
            sortTypes,
            modalType,
            exportPDPs,
            isExportLoading,
            isExportFetching,
            handleExportPDPs,
            isModalOpen,
            deletePDPs,
            setDataToSubmit,
            setLanguages,
            handleDelete,
            caseInsensitiveSort,
            handleOpenModal,
            setModalType,
            setIsModalOpen,
        }),
        [
            languages,
            filters,
            retailer,
            lang,
            retailers,
            isRetailersLoading,
            isRetailerError,
            selectedPDPs,
            dateInWeeks,
            dataToSubmit,
            columns,
            table,
            pdpData,
            isPDPLoading,
            isPDPFetching,
            isPDPError,
            todaysDate,
            isExportLoading,
            isExportFetching,
            isDeleteLoading,
            isDeleteOpen,
            deleteInitialRef,
            isDeleteMultiple,
            companyInfo,
            userInfo,
            defaultColumn,
            data,
            sortTypes,
            modalType,
            isModalOpen,
        ],
    );

    return props;
};

export type TProductDetailsHooks = ReturnType<typeof useProductDetails>;

export default useProductDetails;
